import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { mergeTimelineItems } from './mergeTimeline';
import { bablerWhatsAppSource } from './source/bablerWhatsAppSource';
import { WhatsAppChatSource, WhatsAppChatSummary, WhatsAppTimelineItem } from './types';

/**
 * Loads the user's WhatsApp chats, opens one (newest page), and pages older history.
 * Depends only on the `WhatsAppChatSource` interface. Errors never throw out of here:
 * a failed list keeps the previous list, a failed first load is `hasError` (blocking) and a
 * failed older page is `olderError` (the loaded messages stay).
 */
export const useWhatsAppChats = (source: WhatsAppChatSource = bablerWhatsAppSource) => {
    const [chats, setChats] = useState<WhatsAppChatSummary[]>([]);
    const [activeChatId, setActiveChatId] = useState<string | null>(null);
    const [items, setItems] = useState<WhatsAppTimelineItem[]>([]);
    const [nextBefore, setNextBefore] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingOlder, setIsLoadingOlder] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [olderError, setOlderError] = useState(false);

    // Monotonic token: bumped by open/close/unmount so a superseded response is discarded.
    const tokenRef = useRef(0);
    // Same idea for the list refresh, so an older refresh can never overwrite a newer one.
    const listTokenRef = useRef(0);
    const olderInFlightRef = useRef(false);
    const cursorRef = useRef<{ chatId: string | null; nextBefore: number | null }>({ chatId: null, nextBefore: null });
    // Synced in the commit phase, never during render (a replayed render must not leak into the ref).
    useLayoutEffect(() => {
        cursorRef.current = { chatId: activeChatId, nextBefore };
    }, [activeChatId, nextBefore]);

    useEffect(
        () => () => {
            tokenRef.current += 1;
            listTokenRef.current += 1;
        },
        [],
    );

    const loadChats = useCallback(() => {
        listTokenRef.current += 1;
        const token = listTokenRef.current;
        source
            .listChats()
            .catch(() => null)
            .then((list) => {
                if (token !== listTokenRef.current || list === null) return;
                setChats(list);
            });
    }, [source]);

    const reset = useCallback(() => {
        tokenRef.current += 1;
        olderInFlightRef.current = false;
        setItems([]);
        setNextBefore(null);
        setIsLoading(false);
        setIsLoadingOlder(false);
        setHasError(false);
        setOlderError(false);
    }, []);

    const open = useCallback(
        (chatId: string) => {
            reset();
            const token = tokenRef.current;
            setActiveChatId(chatId);
            setIsLoading(true);
            source
                .getTimeline(chatId)
                .then((page) => {
                    if (token !== tokenRef.current) return;
                    setItems(mergeTimelineItems([], page.items));
                    setNextBefore(page.nextBefore);
                })
                .catch(() => {
                    if (token === tokenRef.current) setHasError(true);
                })
                .finally(() => {
                    if (token === tokenRef.current) setIsLoading(false);
                });
        },
        [source, reset],
    );

    const loadOlder = useCallback(() => {
        const { chatId, nextBefore: cursor } = cursorRef.current;
        if (!chatId || cursor === null || olderInFlightRef.current) return;
        const token = tokenRef.current;
        olderInFlightRef.current = true;
        setHasError(false);
        setOlderError(false);
        setIsLoadingOlder(true);
        source
            .getTimeline(chatId, cursor)
            .then((page) => {
                if (token !== tokenRef.current) return;
                setItems((current) => mergeTimelineItems(current, page.items));
                setNextBefore(page.nextBefore);
            })
            .catch(() => {
                if (token === tokenRef.current) setOlderError(true);
            })
            .finally(() => {
                if (token !== tokenRef.current) return;
                olderInFlightRef.current = false;
                setIsLoadingOlder(false);
            });
    }, [source]);

    const close = useCallback(() => {
        reset();
        setActiveChatId(null);
    }, [reset]);

    return {
        chats,
        activeChatId,
        items,
        hasMore: nextBefore !== null,
        isLoading,
        isLoadingOlder,
        hasError,
        olderError,
        loadChats,
        open,
        loadOlder,
        close,
    };
};
